import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface ProfileData {
  id: string;
  full_name: string;
  phone: string | null;
  color: string | null;
  password_reset_required: boolean | null;
  specialties: string[] | null;
  specialty: string | null;
  registration_number: string | null;
  is_active: boolean | null;
  clinic_roles: Array<{
    role: string;
    clinics: { name: string } | null;
  }>;
}

export const useProfile = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["user-profile", user?.id],
    queryFn: async (): Promise<ProfileData | null> => {
      if (!user?.id) return null;

      const [profileResult, rolesResult] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, full_name, phone, color, password_reset_required, specialties, specialty, registration_number, is_active")
          .eq("id", user.id)
          .maybeSingle(),
        supabase
          .from("user_clinic_roles")
          .select("role, clinics(name)")
          .eq("user_id", user.id),
      ]);

      if (profileResult.error) throw profileResult.error;
      if (!profileResult.data) return null;

      if (rolesResult.error) {
        console.error("Failed to fetch user_clinic_roles:", rolesResult.error);
      }

      const profileData = profileResult.data;
      
      return {
        id: profileData.id,
        full_name: profileData.full_name,
        phone: profileData.phone,
        color: profileData.color,
        password_reset_required: profileData.password_reset_required,
        specialties: profileData.specialties,
        specialty: profileData.specialty,
        registration_number: profileData.registration_number,
        is_active: profileData.is_active,
        clinic_roles: rolesResult.data || [],
      };
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes - profile data rarely changes
    gcTime: 10 * 60 * 1000,   // 10 minutes
  });
};
